import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Button,
  Row,
  Col,
  Form,
  Input,
  Typography,
  Table,
  Switch,
  Space
} from 'antd'
import { SearchOutlined, PlusOutlined } from '@ant-design/icons'
import StampCustomerModal, { Customer } from './StampCustomerModal'

import { debounce } from 'lodash'
import ClickableTitle from './../Utils/ClickableTitle'
import { LoyaltyCustomerListItem } from './types'
import BulkAddDrawer from './BulkAddDrawer'
import TableSorter from 'components/Utils/TableSorter'
import { FormItemCol } from 'components/Common'
import { ApolloQueryResult } from '@apollo/client'
import { HandleTableSort, LoyaltyCustomer } from './LoyaltyOverview'
import styled from '@emotion/styled'
import { CustomSorterRow } from 'components/Common'

interface LoyaltyCustomerProps {
  toggleOptIn: () => void
  data: {
    loyaltyCustomersList: LoyaltyCustomerListItem[]
  }
  customers: LoyaltyCustomer[]
  optInFilter: boolean
  loading: boolean
  customerAggregateRefetch:
    | ((
        variables?:
          | Partial<{
              merchantId: string
              startDate: string
              endDate: string
              storeIds: string[] | []
              email: string
              offset: number
              limit: number
            }>
          | undefined
      ) => Promise<ApolloQueryResult<any>>)
    | undefined
  activeSort: string
  currentPage: number
  setCurrentPage: any
  handleTableSort: ({}: HandleTableSort) =>
    | Promise<ApolloQueryResult<any>>
    | undefined
    | void
  onStampSuccess?: (customerId: string, stampsAdded: number) => void
}

const { Title } = Typography
const { useForm } = Form

const TopLoyaltyCustomers = ({
  loading,
  data,
  customers,
  optInFilter,
  toggleOptIn,
  customerAggregateRefetch,
  activeSort,
  currentPage,
  setCurrentPage,
  handleTableSort,
  onStampSuccess
}: LoyaltyCustomerProps) => {
  const [form] = useForm()
  const navigate = useNavigate()
  const location = useLocation()
  const [stampModalVisible, setStampModalVisible] = useState(false)
  const [modalCustomer, setModalCustomer] = useState<Customer | null>(null)
  const [isDrawerVisible, setIsDrawerVisible] = useState(false)
  const isMounted = useRef(false)

  useEffect(() => {
    if (location.pathname.includes('/loyalty/overview/bulk-actions')) {
      setIsDrawerVisible(true)
    }
  }, [location.pathname])

  const triggerCustomerSearchFilter = (searchParam: string | null) => {
    if (searchParam === null) return
    const lowerCased = searchParam.toLowerCase()
    setCurrentPage(1)

    customerAggregateRefetch!({
      email: `%${lowerCased}%`,
      offset: 0
    })
  }
  const debounceSetCustomerSearchFilter = debounce(
    triggerCustomerSearchFilter,
    500
  )

  useEffect(() => {
    return debounceSetCustomerSearchFilter.cancel()
  }, [])

  useEffect(() => {
    if (isMounted.current) {
      triggerCustomerSearchFilter(null)
    } else {
      isMounted.current = true
    }
  }, [activeSort])

  const dataColumns = [
    {
      title: 'Customer',
      dataIndex: 'firstName',
      key: 'name',
      render: (_firstName: string, record: LoyaltyCustomerListItem) => {
        return (
          <>
            <Button
              icon={<PlusOutlined />}
              className='_mr-8'
              onClick={() => {
                setStampModalVisible(true)
                setModalCustomer(record)
              }}
            ></Button>
            <span>{`${record.firstName} ${record.lastName}`}</span>
          </>
        )
      }
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email: string) => <span>{email}</span>
    },
    {
      title: 'Current Stamps',
      dataIndex: 'currentStamps',
      key: 'currentStamps',
      render: (v: number) => <span>{v ?? 0}</span>
    },
    {
      title: 'Current Rewards',
      dataIndex: 'currentRewards',
      key: 'currentRewards',
      render: (v: number) => <span>{v ?? 0}</span>
    },
    {
      title: 'Rewards Redeemed (Online)',
      dataIndex: 'periodRewardsOnline',
      key: 'periodRewardsOnline',
      render: (v: number) => <span>{v ?? 0}</span>
    },
    {
      title: 'Rewards Redeemed (In Store)',
      dataIndex: 'periodRewardsInStore',
      key: 'periodRewardsInStore',
      render: (v: number) => <span>{v ?? 0}</span>
    },
    {
      title: 'All Time Rewards Redeemed (Online)',
      dataIndex: 'lifetimeRewardsOnline',
      key: 'lifetimeRewardsOnline',
      render: (v: number) => <span>{v ?? 0}</span>
    },
    {
      title: 'All Time Rewards Redeemed (In Store)',
      dataIndex: 'lifetimeRewardsInStore',
      key: 'lifetimeRewardsInStore',
      render: (v: number) => <span>{v ?? 0}</span>
    },
    {
      title: 'Lifetime Stamps (Online)',
      dataIndex: 'lifetimeStampsOnline',
      key: 'lifetimeStampsOnline',
      render: (v: number) => <span>{v ?? 0}</span>
    },
    {
      title: 'Lifetime Stamps (In Store)',
      dataIndex: 'lifetimeStampsInStore',
      key: 'lifetimeStampsInStore',
      render: (v: number) => <span>{v ?? 0}</span>
    },
    {
      title: (
        <CustomSorterRow className='_mb-0' align='middle' gutter={8}>
          <ClickableTitle
            onClick={() =>
              handleTableSort({
                sortBy: 'orders',
                sortDirection: 'desc',
                id: 'total-spent-desc'
              })
            }
            title='Total spent'
            type='descending'
          />
          <Col>
            <Space direction='vertical' size={0} className='_mb-2'>
              <div className='_center-vertical'>
                <TableSorter
                  onClick={() =>
                    handleTableSort({
                      sortBy: 'orders',
                      sortDirection: 'asc',
                      id: 'total-spent-asc'
                    })
                  }
                  type='asc'
                  isActive={activeSort === 'total-spent-asc'}
                />
              </div>
              <div className='_center-vertical'>
                <TableSorter
                  onClick={() =>
                    handleTableSort({
                      sortBy: 'orders',
                      sortDirection: 'desc',
                      id: 'total-spent-desc'
                    })
                  }
                  type='desc'
                  isActive={activeSort === 'total-spent-desc'}
                />
              </div>
            </Space>
          </Col>
        </CustomSorterRow>
      ),
      dataIndex: 'totalSpent',
      key: 'total',
      render: (totalSpent: number) => <span>£{totalSpent || 0.0}</span>
    }
  ]

  const handleDrawerVisibility = (visible: boolean) => {
    setIsDrawerVisible(visible)
    if (visible) {
      navigate('/loyalty/overview/bulk-actions')
    } else {
      navigate('/loyalty/overview')
    }
  }

  return (
    <>
      <Form form={form} className='_mb-24'>
        <BulkAddDrawer
          onClose={() => handleDrawerVisibility(false)}
          visible={isDrawerVisible}
        />
        <Row justify='space-between' className='_mt-32'>
          <Col>
            <Row align='middle' className='_mb-0'>
              <LoyaltyCustomerTitle level={3} className='_mb-0 _pr-24'>
                Top loyalty customers
              </LoyaltyCustomerTitle>
              <span className='_pl-24'>
                Show only customers who've opted in
              </span>
              <Switch
                checked={optInFilter}
                onChange={toggleOptIn}
                className='_ml-12'
              />
            </Row>
          </Col>
          <Row className='_mb-0' align='middle'>
            <FormItemCol>
              <Form.Item name='query' className='control _mb-0'>
                <StyledInput
                  prefix={<SearchOutlined className='site-form-item-icon' />}
                  placeholder='Search by email'
                  onChange={(e: any) =>
                    debounceSetCustomerSearchFilter(e.target.value)
                  }
                />
              </Form.Item>
            </FormItemCol>
            <Col>
              <Button onClick={() => handleDrawerVisibility(true)}>
                Bulk Actions
              </Button>
            </Col>
          </Row>
        </Row>
      </Form>
      <Table
        data-testid='topLoyaltyCustomersTable'
        dataSource={data?.loyaltyCustomersList}
        columns={dataColumns}
        loading={loading}
        scroll={{ x: true }}
        pagination={{
          defaultPageSize: 100,
          position: ['bottomCenter'],
          showSizeChanger: true,
          current: currentPage,
          total: data.loyaltyCustomersList.length,
          onChange: (page) => {
            setCurrentPage(page)
          }
        }}
      />
      <StampCustomerModal
        customer={modalCustomer}
        visible={stampModalVisible}
        onClose={() => {
          setStampModalVisible(false)
          setModalCustomer(null)
        }}
        onSuccess={(stampsAdded) => {
          if (modalCustomer?.id) {
            onStampSuccess?.(modalCustomer.id, stampsAdded)
          }
        }}
      />
    </>
  )
}

const StyledInput = styled(Input)`
  width: 280px;
`

const LoyaltyCustomerTitle = styled(Title)`
  border-right: #f3f2f4 solid 1px;
`

export default TopLoyaltyCustomers
